import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { Copy, Check, CreditCard, Image as ImageIcon, Loader2, Send, MessageCircle, Facebook } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { StoryCard } from './StoryCard';
import { QRPoster } from './QRPoster';
import { API, joinLink, shareLink, nextBadge } from './joinConfig';
import { RecruiterBadge } from './RecruiterBadge';
import { PaymentPanel } from './PaymentPanel';

export const JoinSuccess = ({ data }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const storyRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refCount, setRefCount] = useState(0);
  const [badge, setBadge] = useState(null);
  const nb = nextBadge(refCount);
  const num = data.member_number;
  const link = joinLink(num);
  const share = shareLink(num);
  const msg = t({ en: `I just joined NDP 🇧🇩 Gen Z is building a new Bangladesh. Join me in 2 minutes & get your digital ID:`, bn: `আমি এনডিপিতে যোগ দিয়েছি 🇧🇩 নতুন বাংলাদেশ গড়তে ২ মিনিটে যোগ দিন:` });

  useEffect(() => {
    fetch(`${API}/ref/${num}`).then(r => r.json()).then(d => { setRefCount(d.referrals || 0); setBadge(d.badge); }).catch(() => {});
  }, [num]);

  const copy = async () => {
    await navigator.clipboard.writeText(link).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const storyImage = async () => {
    setBusy(true);
    try {
      const canvas = await html2canvas(storyRef.current, { scale: 3, backgroundColor: '#0B0B0F'  });
      const blob = await new Promise((r) => canvas.toBlob(r, 'image/png'));
      const file = new File([blob], `NDP_${num}_story.png`, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: `${msg} ${link}` }).catch(() => {});
      } else {
        const a = document.createElement('a');
        a.download = file.name;
        a.href = URL.createObjectURL(blob);
        a.click();
      }
    } finally {
      setBusy(false);
    }
  };

  const enc = encodeURIComponent;
  const shares = [
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, cls: 'bg-[#25D366] text-black', href: `https://wa.me/?text=${enc(`${msg} ${share}`)}` },
    { id: 'facebook', label: 'Facebook', icon: Facebook, cls: 'bg-[#1877F2] text-white', href: `https://www.facebook.com/sharer/sharer.php?u=${enc(share)}` },
    { id: 'messenger', label: 'Messenger', icon: Send, cls: 'bg-gradient-to-r from-[#00B2FF] to-[#A033FF] text-white', href: `fb-messenger://share/?link=${enc(share)}` },
    { id: 'telegram', label: 'Telegram', icon: Send, cls: 'bg-[#229ED9] text-white', href: `https://t.me/share/url?url=${enc(share)}&text=${enc(msg)}` },
  ];

  return (
    <div className="rounded-[2rem] border border-party-green/30 bg-white p-6 backdrop-blur-xl sm:p-8" data-testid="join-success">
      <p className="font-join text-xs font-bold uppercase tracking-[0.3em] text-party-green">{data.duplicate ? t({ en: 'Welcome back', bn: 'আবার স্বাগতম' }) : t({ en: "You're in", bn: 'আপনি যুক্ত হয়েছেন' })}</p>
      <h2 className="font-join mt-2 text-3xl font-black text-gray-900 sm:text-4xl">{data.full_name?.split(' ')[0]}, {t({ en: 'welcome to NDP.', bn: 'এনডিপিতে স্বাগতম।' })}</h2>
      <div className="mt-5 rounded-2xl bg-gray-50 p-4">
        <p className="text-xs text-gray-500">{t({ en: 'Your member number', bn: 'আপনার সদস্য নম্বর' })}</p>
        <p className="font-join text-2xl font-black tracking-wider text-party-red" data-testid="join-member-number">{num}</p>
      </div>
      <button onClick={() => navigate(`/member-card?mobile=${data.mobile}`)} className="font-join mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-gray-900 py-4 font-bold text-white transition-transform duration-200 hover:scale-[1.02]" data-testid="join-view-id-card-btn">
        <CreditCard className="h-5 w-5" /> {t({ en: 'View & download my ID card', bn: 'আমার আইডি কার্ড দেখুন ও ডাউনলোড করুন' })}
      </button>

      <PaymentPanel memberNumber={num} mobile={data.mobile} />

      <div className="mt-8 border-t border-gray-200 pt-6">
        <h3 className="font-join text-xl font-bold text-gray-900">{t({ en: 'Now bring your squad', bn: 'এবার বন্ধুদের নিয়ে আসুন' })}</h3>
        <p className="mt-1 text-sm text-gray-500">{t({ en: 'Every friend who joins with your link puts you higher on the Top Recruiters board.', bn: 'আপনার লিংকে যোগ দেওয়া প্রতিটি বন্ধু আপনাকে টপ রিক্রুটার বোর্ডে উপরে তুলবে।' })}</p>
        <p className="mt-3 text-sm text-gray-900" data-testid="join-referral-count">{t({ en: 'Friends recruited:', bn: 'যোগ করা বন্ধু:' })} <span className="font-join font-black text-party-green">{refCount}</span> <RecruiterBadge badge={badge} /></p>
        {nb && <p className="mt-1 text-xs text-gray-500" data-testid="join-next-badge">{t({ en: `${nb[1].min - refCount} more to unlock ${nb[1].label}`, bn: `${nb[1].bn} পেতে আরও ${nb[1].min - refCount} জন` })}</p>}
        <div className="mt-4 flex items-center gap-2 rounded-2xl border border-gray-200 bg-gray-50 p-2 pl-4">
          <span className="min-w-0 flex-1 truncate text-sm text-gray-700" data-testid="join-referral-link">{link}</span>
          <button onClick={copy} className="flex shrink-0 items-center gap-1 rounded-xl bg-party-green px-3 py-2 text-sm font-bold text-white" data-testid="join-copy-link-btn">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}{copied ? t({ en: 'Copied', bn: 'কপি হয়েছে' }) : t({ en: 'Copy', bn: 'কপি' })}
          </button>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {shares.map((s) => (
            <a key={s.id} href={s.href} target="_blank" rel="noopener noreferrer" className={`flex items-center justify-center gap-1.5 rounded-full py-3 text-sm font-bold transition-transform duration-200 hover:scale-[1.04] ${s.cls}`} data-testid={`join-share-${s.id}`}>
              <s.icon className="h-4 w-4" /> {s.label}
            </a>
          ))}
        </div>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
          <button onClick={storyImage} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-party-red to-[#FF8A00] px-5 py-3 text-sm font-bold text-white transition-transform duration-200 hover:scale-[1.03]" data-testid="join-story-card-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
            {t({ en: 'Share "I joined" story', bn: '"আমি যোগ দিয়েছি" স্টোরি শেয়ার' })}
          </button>
          <QRPoster refCode={num} />
        </div>
      </div>
      <div style={{ position: 'fixed', left: -10000, top: 0 }} aria-hidden>
        <StoryCard ref={storyRef} name={data.full_name} memberNumber={num} district={data.district} link={link} />
      </div>
    </div>
  );
};
